import posthog from 'posthog-js';

// Utility to safely check if PostHog is available
const isPostHogAvailable = (): boolean => {
  return typeof window !== 'undefined' && 
         posthog && 
         typeof posthog.capture === 'function' &&
         !!process.env.NEXT_PUBLIC_POSTHOG_KEY;
};

// TypeScript interfaces for event properties
export interface UserLifecycleProperties {
  email: string;
  auth_method?: string;
  signup_timestamp?: string;
  login_timestamp?: string;
  logout_timestamp?: string;
  session_duration_ms?: number;
}

export interface DocumentProperties {
  document_id: string;
  file_name: string;
  file_size_bytes?: number;
  file_size_mb?: number;
  file_type?: string;
  client_id?: string;
  user_email?: string;
  upload_timestamp?: string;
  processing_type?: string;
  start_timestamp?: string;
}

export interface ProcessingProperties {
  document_id: string;
  progress_percentage: number;
  current_step: string;
  success?: boolean;
  processing_time_ms?: number;
  processing_time_minutes?: number;
  extracted_fields_count?: number;
  error_message?: string;
  failure_step?: string;
}

export interface ClientProperties {
  client_id: string;
  client_name: string;
  user_email: string;
  document_count?: number;
  initial_document_count?: number;
  updated_fields?: string[];
}

export interface FormProperties {
  document_id: string;
  form_type: string;
  fields_modified?: string[];
  fields_modified_count?: number;
  field_name?: string;
  old_value?: string | null;
  new_value?: string | null;
  user_email: string;
  client_id?: string;
}

export interface QueryProperties {
  document_id: string;
  query: string;
  query_length?: number;
  response_time_ms: number;
  results_count: number;
  user_email: string;
}

export interface PerformanceProperties {
  endpoint?: string;
  method?: string;
  response_time_ms: number;
  status_code?: number;
  success?: boolean;
  page_name?: string;
  load_time_ms?: number;
  user_email?: string;
}

export interface ErrorProperties {
  error_type: string;
  error_message: string;
  error_context: string;
  user_email?: string;
  [key: string]: unknown;
}

export interface WorkflowProperties {
  workflow_name: string;
  step_name: string;
  step_duration_ms: number;
  user_email: string;
  [key: string]: unknown;
}

// User lifecycle events
export const trackUserSignUp = (email: string, authMethod: string = 'email', properties?: Partial<UserLifecycleProperties>) => {
  if (!isPostHogAvailable()) return;
  
  try {
    posthog.capture('user_signed_up', {
      email,
      auth_method: authMethod,
      signup_timestamp: new Date().toISOString(),
      ...properties
    });
  } catch (error) {
    console.error('Failed to track user signup:', error);
  }
};

export const trackUserSignIn = (email: string, authMethod: string = 'email', properties?: Partial<UserLifecycleProperties>) => {
  if (!isPostHogAvailable()) return;
  
  try {
    posthog.capture('user_signed_in', {
      email,
      auth_method: authMethod,
      login_timestamp: new Date().toISOString(),
      ...properties
    });
  } catch (error) {
    console.error('Failed to track user signin:', error);
  }
};

export const trackUserSignOut = (sessionDuration?: number) => {
  if (!isPostHogAvailable()) return;
  
  try {
    posthog.capture('user_signed_out', {
      session_duration_ms: sessionDuration,
      logout_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track user signout:', error);
  }
};

export const trackUserProfileUpdate = (updatedFields: string[], userEmail: string) => {
  if (!isPostHogAvailable()) return;
  
  try {
    posthog.capture('user_profile_updated', {
      updated_fields: updatedFields,
      user_email: userEmail,
      update_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track user profile update:', error);
  }
};

// Document lifecycle events
export const trackDocumentUpload = (documentId: string, fileName: string, fileSize: number, clientId?: string, userEmail?: string) => {
  if (!isPostHogAvailable()) return;
  
  try {
    posthog.capture('document_uploaded', {
      document_id: documentId,
      file_name: fileName,
      file_size_bytes: fileSize,
      file_size_mb: Math.round(fileSize / (1024 * 1024) * 100) / 100,
      file_type: fileName.split('.').pop()?.toLowerCase(),
      client_id: clientId,
      user_email: userEmail,
      upload_timestamp: new Date().toISOString()
    } as DocumentProperties & { upload_timestamp: string });
  } catch (error) {
    console.error('Failed to track document upload:', error);
  }
};

export const trackDocumentProcessingStarted = (documentId: string, fileName: string, clientId?: string, processingType?: string) => {
  if (!isPostHogAvailable()) return;
  
  try {
    posthog.capture('document_processing_started', {
      document_id: documentId,
      file_name: fileName,
      client_id: clientId,
      processing_type: processingType || 'k1_extraction',
      start_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track document processing started:', error);
  }
};

export const trackDocumentProcessingProgress = (documentId: string, progress: number, currentStep: string) => {
  if (!isPostHogAvailable()) return;
  try {
    posthog.capture('document_processing_progress', {
      document_id: documentId,
      progress_percentage: progress,
      current_step: currentStep,
      progress_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track document processing progress:', error);
  }
};

export const trackDocumentProcessingCompleted = (documentId: string, success: boolean, processingTimeMs: number, extractedFields?: number) => {
  if (!isPostHogAvailable()) return;
  try {
    posthog.capture('document_processing_completed', {
      document_id: documentId,
      success,
      processing_time_ms: processingTimeMs,
      processing_time_minutes: Math.round(processingTimeMs / 60000 * 100) / 100,
      extracted_fields_count: extractedFields,
      completion_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track document processing completed:', error);
  }
};

export const trackDocumentProcessingFailed = (documentId: string, errorMessage: string, failureStep: string, processingTimeMs?: number) => {
  if (!isPostHogAvailable()) return;
  try {
    posthog.capture('document_processing_failed', {
      document_id: documentId,
      error_message: errorMessage,
      failure_step: failureStep,
      processing_time_ms: processingTimeMs,
      failure_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track document processing failed:', error);
  }
};

// Client management events
export const trackClientCreated = (clientId: string, clientName: string, userEmail: string, documentCount?: number) => {
  if (!isPostHogAvailable()) return;
  try {
    posthog.capture('client_created', {
      client_id: clientId,
      client_name: clientName,
      user_email: userEmail,
      initial_document_count: documentCount || 0,
      creation_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track client created:', error);
  }
};

export const trackClientViewed = (clientId: string, clientName: string, documentCount: number, userEmail: string) => {
  if (!isPostHogAvailable()) return;
  
  try {
    posthog.capture('client_viewed', {
      client_id: clientId,
      client_name: clientName,
      document_count: documentCount,
      user_email: userEmail,
      view_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track client viewed:', error);
  }
};

export const trackClientUpdated = (clientId: string, updatedFields: string[], userEmail: string) => {
  if (!isPostHogAvailable()) return;
  try {
    posthog.capture('client_updated', {
      client_id: clientId,
      updated_fields: updatedFields,
      user_email: userEmail,
      update_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track client updated:', error);
  }
};

// Form and review events
export const trackFormDataSaved = (documentId: string, formType: string, fieldsModified: string[], userEmail: string, clientId?: string) => {
  if (!isPostHogAvailable()) return;
  try {
    posthog.capture('form_data_saved', {
      document_id: documentId,
      form_type: formType,
      fields_modified: fieldsModified,
      fields_modified_count: fieldsModified.length,
      user_email: userEmail,
      client_id: clientId,
      save_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track form data saved:', error);
  }
};

export const trackFormFieldUpdated = (documentId: string, fieldName: string, oldValue: string | null, newValue: string | null, userEmail: string) => {
  if (!isPostHogAvailable()) return;
  try {
    posthog.capture('form_field_updated', {
      document_id: documentId,
      field_name: fieldName,
      old_value: oldValue,
      new_value: newValue,
      user_email: userEmail,
      field_update_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track form field updated:', error);
  }
};

export const trackDocumentReviewStarted = (documentId: string, fileName: string, userEmail: string, clientId?: string) => {
  if (!isPostHogAvailable()) return;
  try {
    posthog.capture('document_review_started', {
      document_id: documentId,
      file_name: fileName,
      user_email: userEmail,
      client_id: clientId,
      review_start_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track document review started:', error);
  }
};

export const trackDocumentReviewCompleted = (documentId: string, reviewTimeMs: number, fieldsModified: number, userEmail: string) => {
  if (!isPostHogAvailable()) return;
  try {
    posthog.capture('document_review_completed', {
      document_id: documentId,
      review_time_ms: reviewTimeMs,
      review_time_minutes: Math.round(reviewTimeMs / 60000 * 100) / 100,
      fields_modified_count: fieldsModified,
      user_email: userEmail,
      review_completion_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track document review completed:', error);
  }
};

// RAG Query and search events
export const trackDocumentQuery = (documentId: string, query: string, responseTime: number, resultsCount: number, userEmail: string) => {
  if (!isPostHogAvailable()) return;
  try {
    posthog.capture('document_queried', {
      document_id: documentId,
      query: query,
      query_length: query.length,
      response_time_ms: responseTime,
      results_count: resultsCount,
      user_email: userEmail,
      query_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track document query:', error);
  }
};

// Export and download events
export const trackDataExport = (exportType: string, documentIds: string[], userEmail: string, clientId?: string, documentCount?: number) => {
  if (!isPostHogAvailable()) return;
  try {
    posthog.capture('data_exported', {
      export_type: exportType,
      document_ids: documentIds,
      document_count: documentCount || documentIds.length,
      user_email: userEmail,
      client_id: clientId,
      export_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track data export:', error);
  }
};

export const trackFileDownload = (fileName: string, fileType: string, downloadSource: string, userEmail: string) => {
  if (!isPostHogAvailable()) return;
  try {
    posthog.capture('file_downloaded', {
      file_name: fileName,
      file_type: fileType,
      download_source: downloadSource,
      user_email: userEmail,
      download_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track file download:', error);
  }
};

export const trackBulkDownload = (clientId: string, documentCount: number, totalSizeMB: number, userEmail: string) => {
  if (!isPostHogAvailable()) return;
  try {
    posthog.capture('bulk_download_started', {
      client_id: clientId,
      document_count: documentCount,
      total_size_mb: totalSizeMB,
      user_email: userEmail,
      bulk_download_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track bulk download:', error);
  }
};

// Business workflow events
export const trackWorkflowStepCompleted = (workflowName: string, stepName: string, stepDuration: number, userEmail: string, metadata?: Record<string, unknown>) => {
  if (!isPostHogAvailable()) return;
  try {
    posthog.capture('workflow_step_completed', {
      workflow_name: workflowName,
      step_name: stepName,
      step_duration_ms: stepDuration,
      user_email: userEmail,
      step_completion_timestamp: new Date().toISOString(),
      ...metadata
    });
  } catch (error) {
    console.error('Failed to track workflow step completed:', error);
  }
};

export const trackFeatureUsage = (featureName: string, usageContext: string, userEmail: string, properties?: Record<string, unknown>) => {
  if (!isPostHogAvailable()) return;
  
  try {
    posthog.capture('feature_used', {
      feature_name: featureName,
      usage_context: usageContext,
      user_email: userEmail,
      feature_usage_timestamp: new Date().toISOString(),
      ...properties
    });
  } catch (error) {
    console.error('Failed to track feature usage:', error);
  }
};

// Performance and technical events
export const trackAPICall = (endpoint: string, method: string, responseTime: number, statusCode: number, userEmail?: string) => {
  if (!isPostHogAvailable()) return;
  try {
    posthog.capture('api_call_completed', {
      endpoint,
      method,
      response_time_ms: responseTime,
      status_code: statusCode,
      success: statusCode >= 200 && statusCode < 400,
      user_email: userEmail,
      api_call_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track API call:', error);
  }
};

export const trackPageLoadTime = (pageName: string, loadTime: number, userEmail?: string) => {
  if (!isPostHogAvailable()) return;
  
  try {
    posthog.capture('page_load_performance', {
      page_name: pageName,
      load_time_ms: loadTime,
      user_email: userEmail,
      page_load_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track page load time:', error);
  }
};

// Error and exception tracking
export const trackError = (errorType: string, errorMessage: string, context: string, userEmail?: string, metadata?: Record<string, unknown>) => {
  if (!isPostHogAvailable()) return;
  
  try {
    // Get current user email from PostHog if not provided
    let finalUserEmail = userEmail;
    if (!finalUserEmail && posthog && typeof posthog.get_distinct_id === 'function') {
      // Try to get user email from PostHog's current user properties
      const currentUser = posthog.get_property('$email');
      if (currentUser) {
        finalUserEmail = currentUser;
      }
    }

    posthog.capture('error_occurred', {
      error_type: errorType,
      error_message: errorMessage,
      error_context: context,
      user_email: finalUserEmail,
      error_timestamp: new Date().toISOString(),
      page_url: typeof window !== 'undefined' ? window.location.href : undefined,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      distinct_id: posthog ? posthog.get_distinct_id() : undefined,
      ...metadata
    } as ErrorProperties & { 
      error_timestamp: string; 
      page_url?: string; 
      user_agent?: string;
      distinct_id?: string;
    });
  } catch (error) {
    console.error('Failed to track error event:', error);
  }
};

export const trackUserFeedback = (rating: number, feedback: string, featureContext: string, userEmail: string) => {
  if (!isPostHogAvailable()) return;
  try {
    posthog.capture('user_feedback_submitted', {
      rating,
      feedback,
      feature_context: featureContext,
      user_email: userEmail,
      feedback_timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Failed to track user feedback:', error);
  }
};